import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { calculateIRPF } from '../../utils/irpf';

interface TaxSavingsCardProps {
	/** Aportación anual al plan (€) = aportación mensual × 12, de los ajustes de arriba. */
	annualContribution: number;
	/** Aportación anual máxima deducible (€): para el "podrías ahorrar hasta…" por defecto. */
	maxAnnualContribution: number;
	/** Ingresos brutos anuales del onboarding, si los tenemos. Por defecto, 25-35k. */
	initialIncome?: number | null;
	translate: (key: string) => string;
}

/** Ingreso representativo de cada franja salarial (mismos presets que el onboarding B2C). */
const INCOME_PRESETS = [
	{ value: 20000, labelKey: 'individual.taxSavings.bracket.lt25', label: '< 25.000 €' },
	{ value: 30000, labelKey: 'individual.taxSavings.bracket.25to35', label: '25 - 35.000 €' },
	{ value: 47500, labelKey: 'individual.taxSavings.bracket.35to60', label: '35 - 60.000 €' },
	{ value: 75000, labelKey: 'individual.taxSavings.bracket.gt60', label: '> 60.000 €' }
];

/** Franja por defecto (25-35k) si no viene del onboarding. */
const DEFAULT_INCOME = 30000;

/**
 * Ahorro fiscal en el IRPF por aportar al plan: config a la izquierda (franja salarial)
 * y resultado a la derecha (ahorro anual estimado). Precarga la franja del onboarding si
 * la tenemos (si no, 25-35k). Usa la aportación de los ajustes de arriba; si aún es 0,
 * muestra el ahorro con el máximo deducible ("podrías ahorrar hasta…").
 */
const TaxSavingsCard: React.FC<TaxSavingsCardProps> = ({
	annualContribution,
	maxAnnualContribution,
	initialIncome,
	translate
}) => {
	const [income, setIncome] = useState<number>(
		initialIncome && initialIncome > 0 ? initialIncome : DEFAULT_INCOME
	);

	// Con aportación real la usamos; si aún es 0, ilustramos con el máximo deducible. En ambos
	// casos capamos al máximo deducible: sólo esa parte desgrava, así que el ahorro fiscal no
	// puede calcularse sobre un importe mayor.
	const isPotential = annualContribution <= 0;
	const contribution = isPotential
		? maxAnnualContribution
		: Math.min(annualContribution, maxAnnualContribution);

	const withoutPlan = calculateIRPF(income);
	const withPlan = calculateIRPF(Math.max(0, income - contribution));
	const savings = withoutPlan.total - withPlan.total;

	const eur = (n: number) => `${fmtNum(n, 0)} €`;

	const caption = (
		isPotential
			? translate('individual.taxSavings.resultCaptionPotential') ||
				'Si aportaras el máximo deducible ({contribution}/año), ahorrarías'
			: translate('individual.taxSavings.resultCaption') ||
				'Aportando {contribution}/año a tu plan, tu ahorro fiscal anual sería'
	).replace('{contribution}', eur(contribution));

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:items-stretch">
			{/* Config — franja salarial */}
			<div className="surface-card p-5 space-y-3 lg:col-span-2">
				<div className="space-y-0.5">
					<p className="text-base font-semibold">
						{translate('individual.taxSavings.title') || '¿Cuánto ganas al año?'}
					</p>
					<p className="text-xs text-muted-foreground leading-relaxed">
						{translate('individual.taxSavings.subtitle') ||
							'Aportar a tu plan reduce tu base imponible del IRPF.'}
					</p>
				</div>
				<div className="space-y-2">
					<div className="flex flex-wrap gap-2">
						{INCOME_PRESETS.map((preset) => (
							<button
								key={preset.value}
								type="button"
								onClick={() => setIncome(preset.value)}
								className={cn(
									'rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
									income === preset.value
										? 'border-primary bg-primary/5 text-primary'
										: 'border-border text-muted-foreground hover:bg-muted/40'
								)}
							>
								{translate(preset.labelKey) || preset.label}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Resultado — ahorro fiscal */}
			<div className="surface-card p-5 lg:col-span-3">
				<div className="flex h-full flex-col justify-center space-y-3">
					<div>
						<p className="text-sm text-muted-foreground">{caption}</p>
						<p className="text-3xl font-bold font-heading text-primary mt-1">
							{eur(Math.round(savings))}
						</p>
					</div>
					<div className="border-t border-border/40 pt-3 flex items-baseline justify-between text-sm">
						<span className="text-muted-foreground">
							{translate('individual.taxSavings.quotaLabel') || 'Tu cuota de IRPF pasa de'}
						</span>
						<span className="font-semibold tabular-nums">
							{eur(Math.round(withoutPlan.total))} → {eur(Math.round(withPlan.total))}
						</span>
					</div>
					<p className="text-xs text-muted-foreground leading-relaxed">
						{translate('individual.taxSavings.disclaimer') ||
							'Simulación orientativa basada en tramos IRPF 2025. No constituye asesoramiento fiscal.'}
					</p>
				</div>
			</div>
		</div>
	);
};

export default TaxSavingsCard;
